'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import type { SiteLocale } from '@/lib/marketing/page-metadata';
import {
  preferredSiteLocale,
  readLangCookie,
  translatedPath,
  writeLangCookie,
} from '@/lib/marketing/locale-routing';

type SuggestionCopy = { prompt: string; action: string; dismiss: string };

/**
 * Offers the page in the visitor's language instead of redirecting to it
 * (Google: never auto-redirect between language versions). Shown only when the
 * page has that translation and the visitor hasn't already picked a language;
 * either button saves the pick, so it appears at most once per choice.
 */
export function LanguageSuggestion({
  locale,
  copy,
}: {
  locale: SiteLocale;
  copy: Record<SiteLocale, SuggestionCopy>;
}) {
  const pathname = usePathname() ?? '/';
  const [offer, setOffer] = useState<{ locale: SiteLocale; href: string } | null>(null);

  useEffect(() => {
    const chosen = readLangCookie();
    const preferred =
      chosen ?? preferredSiteLocale(navigator.languages?.join(',') || navigator.language);
    const href = preferred && preferred !== locale ? translatedPath(pathname, preferred) : null;
    setOffer(preferred && href ? { locale: preferred, href } : null);
  }, [pathname, locale]);

  if (!offer) return null;
  const text = copy[offer.locale];

  return (
    <div
      lang={offer.locale}
      role="region"
      aria-label={text.prompt}
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-border bg-background px-4 py-3 text-[14px] shadow-lg sm:inset-x-0"
    >
      <span className="text-muted-foreground">{text.prompt}</span>
      <Link
        href={offer.href}
        hrefLang={offer.locale}
        onClick={() => writeLangCookie(offer.locale)}
        className="font-semibold text-[#EF4444] hover:underline underline-offset-4"
      >
        {text.action}&nbsp;&rarr;
      </Link>
      <button
        type="button"
        onClick={() => {
          writeLangCookie(locale);
          setOffer(null);
        }}
        aria-label={text.dismiss}
        className="ml-auto rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
